import {
  ArrowBackOutlined,
  ButtonDS,
  SendOutlined,
  Spacer,
  TableDS,
  Tag,
  TextCapitalized,
} from "@qivia/ui";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useCallback, useState } from "react";
import "../../../../i18n/i18n";
import {
  DepartmentType,
  DepartmentTypeDisplayed,
} from "../../departments/departmentsApi";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { selectCompany } from "../../homeSlice";
import {
  selectDepartments,
  selectGetDepartmentsByCompanyStatus,
} from "../../departments/departmentsSlice";
import { getDepartmentsByCompanyAsync } from "../../departments/departmentsSlice";
import { useEffect } from "react";
import { EmptyTable } from "@qivia/ui/src/assets/assets";
import type { TableHeaderProps } from "@qivia/ui/src/designSystem/components/table/TableHeader";

export const AddUserDepartmentModal = (props: {
  onSubmit: (value: DepartmentType[]) => void;
  onReturn: () => void;
  isUpdate?: boolean;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);
  const departments = useAppSelector(selectDepartments);
  const getDepartmentsByCompanyStatus = useAppSelector(
    selectGetDepartmentsByCompanyStatus,
  );
  const [rowsChecked, setRowsChecked] = useState<DepartmentType[]>([]);

  const { onSubmit, onReturn } = props;

  const onSelectSubmit = useCallback(() => {
    onSubmit(rowsChecked);
  }, [onSubmit, rowsChecked]);

  const headers = {
    name: {
      text: t("users.modal.department.table.name"),
    },
    vehiclesCount: {
      text: t("users.modal.department.table.vehiclesCount"),
    },
  };

  const render =
    (row: DepartmentType) => (key: keyof DepartmentTypeDisplayed) => {
      switch (key) {
        case "name":
          return (
            <Tag
              textColor={colors["colors/text/black"]}
              backgroundColor={
                colors["colors/surfaces/background/background_level0"]
              }
              text={row[key]}
              borderColor={colors["colors/borders/cells/cells"]}
            />
          );
        case "vehiclesCount":
          return (
            (row[key] ?? "0") +
            " " +
            (Number(row[key]) > 1
              ? t("users.modal.department.table.vehiclesCount.multiple.unit")
              : t("users.modal.department.table.vehiclesCount.unit"))
          );
      }
    };

  useEffect(() => {
    if (company && getDepartmentsByCompanyStatus === "idle") {
      void dispatch(getDepartmentsByCompanyAsync(company.uuid));
    }
  }, [dispatch, company, getDepartmentsByCompanyStatus]);

  return (
    <StyledModalContainer>
      <StyledModalContent>
        <Spacer y={1} />
        <StyledTitle>
          <TextCapitalized>{t(`users.modal.department.title`)}</TextCapitalized>
        </StyledTitle>
        <Spacer y={2} />
        <StyledTable>
          <TableDS<keyof DepartmentTypeDisplayed, DepartmentType>
            data={departments}
            headers={headers as Record<keyof DepartmentType, TableHeaderProps>}
            render={render}
            rowsChecked={rowsChecked}
            setRowsChecked={setRowsChecked}
            height={28}
            width={35}
            header-checkbox-data-cy="header-checkbox-data-cy"
            emptyContent={
              <StyledFlex>
                <Spacer x={1.5} />
                <StyledEmptyTable>
                  <Spacer y={2} />
                  <EmptyTable />
                  <Spacer y={1.5} />
                  <StyledTitleEmptyTable>
                    <TextCapitalized>
                      {t("users.modal.department.listEmpty.title")}
                    </TextCapitalized>
                  </StyledTitleEmptyTable>
                  <Spacer y={1} />
                  <StyledSubText>
                    <TextCapitalized>
                      {t("users.modal.department.listEmpty.body")}
                    </TextCapitalized>
                  </StyledSubText>
                  <Spacer y={2} />
                </StyledEmptyTable>
                <Spacer x={1.5} />
              </StyledFlex>
            }
          />
          <Spacer y={1} />
        </StyledTable>
        <Spacer y={1.5} />
        <StyledButtons>
          <ButtonDS
            leftIcon={<ArrowBackOutlined />}
            text={t("users.modal.role.return")}
            format={"fill"}
            buttonType={"secondary"}
            onClick={onReturn}
          />
          <Spacer x={1} />
          <ButtonDS
            rightIcon={<SendOutlined />}
            text={
              props.isUpdate
                ? t("users.modal.role.update")
                : t("users.modal.role.submit")
            }
            format={"fill"}
            buttonType={"primary"}
            onClick={onSelectSubmit}
            disabled={rowsChecked.length === 0}
          />
        </StyledButtons>
      </StyledModalContent>
    </StyledModalContainer>
  );
};

const StyledButtons = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const StyledModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  overflow-x: hidden;
`;

const StyledModalContent = styled.div`
  flex-direction: column;
  display: flex;
  width: 35rem;
  height: 100%;
  overflow-x: hidden;
  align-items: center;
`;

const StyledTitle = styled.div`
  ${typographies["Header/H1"]}
  text-align: center;
  line-height: 2rem;
`;

const StyledFlex = styled.div`
  display: flex;
`;

const StyledTable = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
`;

const StyledTitleEmptyTable = styled.div`
  display: flex;
  ${typographies["Header/H1"]};
  color: ${colors["colors/text/black"]};
`;

const StyledEmptyTable = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  text-align: center;
`;

const StyledSubText = styled.div`
  display: flex;
  ${typographies["Body/S"]};
  color: ${colors["colors/text/darkGrey"]};
`;
